import React from "react";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  BarChart3,
  Bell,
  BookOpen,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  Copy,
  CreditCard,
  Download,
  FileText,
  FolderKanban,
  Gavel,
  Globe,
  Hand,
  Headphones,
  Hourglass,
  Inbox,
  Info,
  KeyRound,
  Landmark,
  LayoutGrid,
  Lightbulb,
  Link2,
  ListChecks,
  Lock,
  Mail,
  MessageSquare,
  MessagesSquare,
  Plus,
  PlusCircle,
  RefreshCw,
  Reply,
  Rocket,
  Send,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Star,
  StarHalf,
  ThumbsUp,
  Timer,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Unlink,
  UserPlus,
  Users,
  Wallet,
  X,
  XCircle,
  Zap,
} from "lucide-react";

/** Material Symbol name → Lucide component (consistent stroke icons app-wide). */
const ICON_MAP = {
  inbox: Inbox,
  warning: AlertTriangle,
  report: TriangleAlert,
  reviews: MessagesSquare,
  star: Star,
  star_half: StarHalf,
  check: Check,
  reply: Reply,
  close: X,
  delete: Trash2,
  remove_shopping_cart: ShoppingCart,
  link_off: Unlink,
  arrow_forward: ArrowRight,
  arrow_back: ArrowLeft,
  sync: RefreshCw,
  rule: ListChecks,
  check_circle: CheckCircle2,
  payments: Wallet,
  menu_book: BookOpen,
  monitoring: Activity,
  trending_up: TrendingUp,
  trending_down: TrendingDown,
  gavel: Gavel,
  north_east: ArrowUpRight,
  info: Info,
  schedule: CalendarDays,
  timer: Timer,
  send: Send,
  support_agent: Headphones,
  add_circle: PlusCircle,
  add: Plus,
  calendar_month: CalendarDays,
  download: Download,
  expand_more: ChevronDown,
  chevron_right: ChevronRight,
  refresh: RefreshCw,
  content_copy: Copy,
  cancel: XCircle,
  analytics: BarChart3,
  insights: Lightbulb,
  bolt: Zap,
  group: Users,
  person_add: UserPlus,
  verified: BadgeCheck,
  hourglass_empty: Hourglass,
  notifications: Bell,
  folder_managed: FolderKanban,
  credit_card: CreditCard,
  account_balance: Landmark,
  lock: Lock,
  waving_hand: Hand,
  link: Link2,
  rocket_launch: Rocket,
  forum: MessageSquare,
  assignment: ClipboardList,
  policy: Shield,
  mail: Mail,
  hub: LayoutGrid,
  today: CalendarDays,
  auto_awesome: Sparkles,
  description: FileText,
  language: Globe,
  security: Shield,
  key: KeyRound,
  verified_user: ShieldCheck,
  contact_support: Headphones,
  thumb_up: ThumbsUp,
  banknote: Banknote,
};

const FILLED_ICONS = new Set([
  "check",
  "check_circle",
  "star",
  "star_half",
  "menu_book",
  "support_agent",
]);

const SIZE_BY_CLASS = {
  "text-xs": 12,
  "text-sm": 14,
  "text-base": 16,
  "text-lg": 18,
  "text-xl": 20,
  "text-2xl": 24,
  "text-3xl": 30,
  "text-4xl": 36,
  "text-5xl": 48,
};

function parseIconSize(className = "") {
  const custom = className.match(/text-\[(\d+)px\]/);
  if (custom) return Number(custom[1]);

  for (const [token, size] of Object.entries(SIZE_BY_CLASS)) {
    if (className.split(/\s+/).includes(token)) return size;
  }

  return 18;
}

const MaterialIcon = ({ name, filled = false, className = "" }) => {
  const Icon = ICON_MAP[name] || CircleHelp;
  const size = parseIconSize(className);
  const useFill = filled && FILLED_ICONS.has(name);

  return (
    <Icon
      size={size}
      strokeWidth={1.75}
      className={`inline-block shrink-0 ${className}`}
      fill={useFill ? "currentColor" : "none"}
      aria-hidden
    />
  );
};

export default MaterialIcon;
